/**
 * MFA Settings Component
 * Allows users to configure TOTP (authenticator app) multi-factor authentication.
 * SMS MFA is not offered — Cognito SMS requires SNS limits and US carrier registration.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Stack,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { AppButton } from '../design-system';
import {
  PhonelinkSetup as TotpIcon,
  CheckCircle as CheckIcon,
  Cancel as DisabledIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import {
  fetchMFAPreference,
  updateMFAPreference,
} from 'aws-amplify/auth';
import { TOTPSetupDialog } from '../dialogs/TOTPSetupDialog';

export const MFASettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [totpEnabled, setTotpEnabled] = useState(false);
  const [legacySmsMfa, setLegacySmsMfa] = useState(false);

  const [totpSetupDialogOpen, setTotpSetupDialogOpen] = useState(false);

  const loadMFASettings = async () => {
    setLoading(true);
    setError(null);

    try {
      const mfaPreference = await fetchMFAPreference();
      const enabled = mfaPreference.enabled || [];

      setTotpEnabled(enabled.includes('TOTP'));
      setLegacySmsMfa(enabled.includes('SMS'));
    } catch (err) {
      console.error('Failed to fetch MFA preference:', err);
      setError(err instanceof Error ? err.message : 'Failed to load MFA settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMFASettings();
  }, []);

  const handleTotpSetupComplete = async () => {
    setTotpSetupDialogOpen(false);
    await loadMFASettings();
    setSuccess('Authenticator app configured successfully');
  };

  const handleTotpDisable = async () => {
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      await updateMFAPreference({ totp: 'DISABLED' });
      setTotpEnabled(false);
      setSuccess('Authenticator app MFA disabled successfully');
    } catch (err) {
      console.error('Failed to disable TOTP MFA:', err);
      setError(err instanceof Error ? err.message : 'Failed to disable authenticator app MFA');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            Multi-Factor Authentication (MFA)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add an extra layer of security by requiring a code from your authenticator app at sign-in.
          </Typography>
        </Box>

        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" fontWeight="medium">
                MFA Status:
              </Typography>
              {totpEnabled ? (
                <Chip
                  label="Enabled"
                  color="success"
                  size="small"
                  icon={<CheckIcon />}
                />
              ) : (
                <Chip
                  label="Disabled"
                  color="default"
                  size="small"
                  icon={<DisabledIcon />}
                />
              )}
              {totpEnabled && (
                <Typography variant="body2" color="text.secondary">
                  Method: Authenticator app
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>

        {legacySmsMfa && !totpEnabled && (
          <Alert severity="warning">
            Your account has SMS MFA from an earlier configuration. Set up an authenticator app below,
            then contact support if you need SMS MFA removed from your account.
          </Alert>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TotpIcon color={totpEnabled ? 'primary' : 'disabled'} />
                  <Box>
                    <Typography variant="h6">Authenticator App</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Use an app like Google Authenticator or Authy
                    </Typography>
                  </Box>
                </Box>
                {totpEnabled ? (
                  <AppButton
                    variant="outlined"
                    color="error"
                    onClick={handleTotpDisable}
                    disabled={updating}
                  >
                    Disable
                  </AppButton>
                ) : (
                  <AppButton
                    variant="contained"
                    onClick={() => setTotpSetupDialogOpen(true)}
                    disabled={updating}
                  >
                    Set Up
                  </AppButton>
                )}
              </Box>

              {totpEnabled && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Authenticator app is configured and active.
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              About MFA
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Enhanced Security"
                  secondary="MFA significantly reduces the risk of unauthorized access"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Authenticator App"
                  secondary="Works offline and does not rely on text message delivery"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Quick Setup"
                  secondary="Scan a QR code with your authenticator app — takes less than a minute"
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Stack>

      <TOTPSetupDialog
        open={totpSetupDialogOpen}
        onClose={() => setTotpSetupDialogOpen(false)}
        onSuccess={handleTotpSetupComplete}
      />
    </Box>
  );
};
